import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { TailSpin, ThreeDots } from "react-loader-spinner";
import { LEAVE_TYPE_V2 } from "../../config/Endpoints";
import Cookies from "js-cookie";
import { getApi, postApi } from "../../utils/api";
import { showSuccess, showError } from "../../v2/utils/toast.ts";
import { getApiErrorMessage } from "../../v2/utils/api-error-message.ts";
import * as Yup from "yup";
import { getUser } from "../../utils/Storage";
import { formatToDateOnly } from "../../utils/helpers";
import { useUser } from "../../v2/lib/rbac/useUser.ts";
import { formatLeaveTypeLabel } from "./utils/leave-type-label";
import { LeaveSegment, resolveLeaveSegment } from "./config/leave-segments";
import { getLeaveSegmentConfig } from "./config/leave-segment-config";
import {
  Badge,
  Button,
  DateRange,
  Modal,
  Select,
  TextArea,
} from "../../v2/components/ui/index.ts";
import { useSyncConstraintValidationMessage } from "../../v2/hooks/use-sync-constraint-validation-message.ts";
import styles from "./leave.module.css";

const APPLY_LEAVE_FORM_ID = "apply-leave-form";
const APPLY_LEAVE_TYPE_ID = "apply-leave-leave-type";
const APPLY_LEAVE_DATE_RANGE_ID = "apply-leave-date-range";

const US_LEAVE_TYPE_PAYLOAD_MAP = {
  PTO_VACATION_REQUEST: "PTO_VACATION_REQUEST",
  "PTO/Vacation Request": "PTO_VACATION_REQUEST",
  "PTO Vacation Request": "PTO_VACATION_REQUEST",
  SickLeave: "SickLeave",
  "Sick Leave": "SickLeave",
  PERSONAL_APPOINTMENT: "PERSONAL_APPOINTMENT",
  "Personal Appointment": "PERSONAL_APPOINTMENT",
  BEREAVEMENT: "BEREAVEMENT",
  Bereavement: "BEREAVEMENT",
  "Bereavement Leave": "BEREAVEMENT",
  MATERNITY: "MATERNITY",
  Maternity: "MATERNITY",
  "Maternity Leave": "MATERNITY",
  PATERNITY: "PATERNITY",
  Paternity: "PATERNITY",
  "Paternity Leave": "PATERNITY",
};

const getUSPayloadLeaveType = (leaveType) => {
  if (!leaveType) return leaveType;

  const directMatch = US_LEAVE_TYPE_PAYLOAD_MAP[leaveType];
  if (directMatch) return directMatch;

  const normalized = String(leaveType).replace(/[^a-zA-Z]/g, "").toLowerCase();
  if (normalized === "ptovacationrequest") return "PTO_VACATION_REQUEST";
  if (normalized === "sickleave") return "SickLeave";
  if (normalized === "personalappointment") return "PERSONAL_APPOINTMENT";
  if (normalized === "bereavement" || normalized === "bereavementleave")
    return "BEREAVEMENT";
  if (normalized === "maternity" || normalized === "maternityleave")
    return "MATERNITY";
  if (normalized === "paternity" || normalized === "paternityleave")
    return "PATERNITY";

  return leaveType;
};

function formatDateMMDDYYYY(d) {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${m}/${day}/${d.getFullYear()}`;
}

function normalizeAllowedLeaveTypesResponse(resData) {
  if (resData == null) return [];
  const direct = resData.allowedLeaveTypes;
  if (Array.isArray(direct)) return direct;
  const nested = resData.data;
  if (Array.isArray(nested)) return nested;
  if (nested && Array.isArray(nested.allowedLeaveTypes))
    return nested.allowedLeaveTypes;
  return [];
}

const PRIVILEGE_LEAVE_TYPES = new Set(["PL", "PrivilegeLeave", "privilegeLeave"]);
const QUARTERLY_LEAVE_TYPES = new Set(["QUARTERLY", "QuarterlyLeave", "quarterlyLeave"]);
const SHORT_LEAVE_TYPES = new Set(["SHORT_LEAVE", "ShortLeave", "shortLeave"]);
const CASUAL_LEAVE_TYPES = new Set(["CL"]);
const WORK_FROM_HOME_LEAVE_TYPES = new Set([
  "WFH",
  "WorkFromHome",
  "WorkFromHomeLeave",
  "workFromHomeLeave",
]);

function isPrivilegeLeaveType(type) {
  return PRIVILEGE_LEAVE_TYPES.has(type);
}
function isQuarterlyLeaveType(type) {
  return QUARTERLY_LEAVE_TYPES.has(type);
}
function isShortLeaveType(type) {
  return SHORT_LEAVE_TYPES.has(type);
}
function isWorkFromHomeLeaveType(type) {
  return WORK_FROM_HOME_LEAVE_TYPES.has(type);
}
function iscasualLeaveType(type) {
  return CASUAL_LEAVE_TYPES.has(type);
}

const LEAVE_BALANCE_KEY_MAP = {
  PL: "privilegeLeave",
  PrivilegeLeave: "privilegeLeave",
  privilegeLeave: "privilegeLeave",
  CL: "casualLeave",
  WFH: "workFromHomeLeave",
  QUARTERLY: "quartelyLeave",
  QuarterlyLeave: "quartelyLeave",
  quarterlyLeave: "quartelyLeave",
  SHORT_LEAVE: "shortLeave",
  ShortLeave: "shortLeave",
  shortLeave: "shortLeave",
};

const ApplyLeave = ({
  fetchList,
  leaveBalance,
  fetchLeaveBalance,
  onWFHSuccess,
  onClose,
  open,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [isListLoading, setIsListLoading] = React.useState(false);
  const [leaveTypeList, setLeaveTypeList] = React.useState([]);
  const [formData, setFormData] = React.useState({
    selectedLeaveType: "",
    selectedConsiderLeaveType: "",
    startDate: "",
    endDate: "",
    isConsiderLeave: false,
    reasonOfLeave: "",
  });
  const [errors, setErrors] = React.useState({});
  const leaveTypeValidityRef = useRef(null);
  const dateRangeValidityRef = useRef(null);
  const userData = JSON.parse(getUser("userData"));
  const user = useUser();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
  const [isLeaveBalanceZero, setIsLeaveBalanceZero] = React.useState(false);
  const accessToken = Cookies.get("accessToken");
  const segment = resolveLeaveSegment({
    location: user?.location ?? userData?.location,
    empType: user?.empType ?? userData?.empType,
  });
  const leaveSegmentConfig = getLeaveSegmentConfig(segment);
  const isUSSegment = segment === LeaveSegment.US;
  const isSQESegment = segment === LeaveSegment.SQE;

  function shouldPromptZeroBalanceForType(leaveType) {
    if (!isSQESegment) return true;
    return (
      isPrivilegeLeaveType(leaveType) ||
      isQuarterlyLeaveType(leaveType) ||
      isShortLeaveType(leaveType)
    );
  }

  function shouldShowLeaveBalanceBadge(leaveType) {
    if (!leaveType || leaveType === "HalfDayLeave") return false;
    if (isSQESegment && isWorkFromHomeLeaveType(leaveType)) return false;
    return Boolean(getLeaveBalance(leaveType));
  }

  const getQuarterEndDate = () => {
    const now = new Date();
    const month = now.getMonth();
    let endMonth;
    if (month <= 3) {
      endMonth = 3;
    } else if (month <= 7) {
      endMonth = 7;
    } else {
      endMonth = 11;
    }
    return new Date(now.getFullYear(), endMonth + 1, 0);
  };

  const isQuarterlyLeave =
    formData.selectedLeaveType === "CL" || formData.selectedLeaveType === "WFH";

  const maxDate = isQuarterlyLeave ? getQuarterEndDate() : null;

  const handleReset = useCallback(() => {
    setFormData({
      selectedLeaveType: "",
      selectedConsiderLeaveType: "",
      startDate: "",
      endDate: "",
      isConsiderLeave: false,
      reasonOfLeave: "",
    });
    setErrors({});
    setIsLoading(false);
  }, []);

  const prevApplyOpen = React.useRef(false);
  useEffect(() => {
    if (prevApplyOpen.current && open === false) {
      handleReset();
    }
    prevApplyOpen.current = open === true;
  }, [open, handleReset]);

  useEffect(() => {
    fetchLeaveType();
  }, []);

  const fetchLeaveType = async () => {
    setIsListLoading(true);
    await getApi(LEAVE_TYPE_V2, accessToken)
      .then((res) => {
        if (res.status === 200) {
          setLeaveTypeList(normalizeAllowedLeaveTypesResponse(res.data));
          setIsListLoading(false);
        }
      })
      .catch((e) => {
        console.log(e);
        setIsListLoading(false);
      });
  };

  const leaveTypeOptions = useMemo(() => {
    const gender = user?.gender?.toLowerCase();

    const filteredLeaveTypes = leaveTypeList.filter((type) => {
      if (type === "MATERNITY") {
        return gender !== "male"; // female or null
      }

      if (type === "PATERNITY") {
        return gender === "male";
      }

      return true;
    });

    return filteredLeaveTypes.map((type) => ({
      value: type,
      label: formatLeaveTypeLabel(type),
    }));
  }, [leaveTypeList, user?.gender]);

  const dateRangeComplete = Boolean(formData.startDate && formData.endDate);
  const dateRangeErrorMessage =
    [errors.startDate, errors.endDate].filter(Boolean).join(" ") || undefined;

  useSyncConstraintValidationMessage(leaveTypeValidityRef, errors.selectedLeaveType);
  useSyncConstraintValidationMessage(dateRangeValidityRef, dateRangeErrorMessage);

  const handleInputChange = (event) => {
    const { name, value, type } = event.target;
    if (type === "date") {
      const year = value.split("-")[0];
      if (year.length <= 4) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          [name]: value,
        }));
      }
    } else {
      if (name === "selectedLeaveType") {
        const balance = getLeaveBalance(value);

        if (value === "HalfDayLeave") {
          const balancePrivilege = getLeaveBalance("PrivilegeLeave");
          const privilegeLeaveBalance = balancePrivilege.find(
            (item) => item.balance === 0,
          );
          if (privilegeLeaveBalance && privilegeLeaveBalance.balance === 0) {
            setIsLeaveBalanceZero(true);
            setIsConfirmModalOpen(true);
          } else {
            setIsLeaveBalanceZero(false);
          }
        } else if (
          shouldPromptZeroBalanceForType(value) &&
          balance &&
          balance.every(
            (item) => item.balance === 0 && item.key !== "HalfDayLeave",
          )
        ) {
          setIsLeaveBalanceZero(true);
          setIsConfirmModalOpen(true);
        } else {
          setIsLeaveBalanceZero(false);
        }
      }
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const onChange = (dates) => {
    const [start, end] = dates;
    setFormData((prevFormData) => ({
      ...prevFormData,
      startDate: start,
      endDate: end,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.startDate;
      delete next.endDate;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    try {
      setErrors({});
      await ApplyLeaveSchema.validate(
        {
          selectedLeaveType: formData.selectedLeaveType,
          startDate: formData.startDate,
          endDate: formData.endDate,
          reasonOfLeave: formData.reasonOfLeave,
        },
        { abortEarly: false },
      );
      setIsLoading(true);

      const requestObj = isUSSegment
        ? {
          considerLeave: formData.isConsiderLeave,
          considerLeaveType: getUSPayloadLeaveType(
            formData.selectedConsiderLeaveType,
          ),
          startDate: formatToDateOnly(formData.startDate),
          endDate: formatToDateOnly(formData.endDate),
          reasonOfLeave: formData.reasonOfLeave,
          typeOfLeave: getUSPayloadLeaveType(formData.selectedLeaveType),
        }
        : {
          employeeId: userData.id,
          leaveType: formData.selectedLeaveType,
          fromDate: formatToDateOnly(formData.startDate),
          toDate: formatToDateOnly(formData.endDate),
          reason: formData.reasonOfLeave,
        };

      await postApi(leaveSegmentConfig.applyEndpoint, requestObj, accessToken)
        .then((res) => {
          setIsLoading(false);
          showSuccess(res?.data?.message ?? "Leave submitted.");
          handleReset();
          onClose?.();
          if (formData.selectedLeaveType === "WFH") {
            onWFHSuccess?.();
          }
          fetchList();
          fetchLeaveBalance();
        })
        .catch((e) => {
          setIsLoading(false);
          showError(getApiErrorMessage(e));
        });
    } catch (error) {
      console.log(error);

      const validationErrors = {};
      error?.inner?.forEach((err) => {
        validationErrors[err.path] = err.message;
      });

      setErrors(validationErrors);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  function getLeaveBalance(selectedLeaveType) {
    const balanceKey = LEAVE_BALANCE_KEY_MAP[selectedLeaveType];
    if (!balanceKey) return null;

    return [
      {
        key: balanceKey,
        balance: leaveBalance?.[balanceKey] ?? 0,
      },
    ];
  }

  const handleConfirmYes = () => {
    setIsConfirmModalOpen(false);
  };

  const handleConfirmNo = () => {
    setIsConfirmModalOpen(false);
    setFormData((prevFormData) => ({
      ...prevFormData,
      selectedLeaveType: "",
    }));
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentMonthFirstDay = new Date(today);
  currentMonthFirstDay.setDate(1);
  const currentYearFirstDay = new Date(today);
  currentYearFirstDay.setMonth(0);
  currentYearFirstDay.setDate(1);

  const usesYearStartRule = leaveSegmentConfig.minDateRule === "yearStart";
  const standardMinDate = new Date(Math.max(currentMonthFirstDay, today));
  const augustLeaveExceptionStart = new Date(2026, 7, 25);
  const augustLeaveExceptionEnd = new Date(2026, 7, 26);
  const augustLeaveExceptionActive =
    today.getFullYear() === 2026 && today.getMonth() === 7;
  const minDate = usesYearStartRule
    ? currentYearFirstDay
    : augustLeaveExceptionActive
      ? augustLeaveExceptionStart
      : standardMinDate;
  const filterLeaveDate = usesYearStartRule || !augustLeaveExceptionActive
    ? undefined
    : (date) => {
        const calendarDate = new Date(date);
        calendarDate.setHours(0, 0, 0, 0);
        const isAugustException =
          calendarDate >= augustLeaveExceptionStart &&
          calendarDate <= augustLeaveExceptionEnd;
        return isAugustException || calendarDate >= standardMinDate;
      };

  useEffect(() => {
    if (!isConfirmModalOpen) return;

    const handleEsc = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener("keydown", handleEsc, true);

    return () => {
      document.removeEventListener("keydown", handleEsc, true);
    };
  }, [isConfirmModalOpen]);

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          handleReset();
          onClose?.();
        }}
        title="Apply Leave"
        size="lg"
        closeOnBackdropClick={false}
        closeOnEscape={false}
        footer={
          <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap w-100">
            <Button
              type="submit"
              form={APPLY_LEAVE_FORM_ID}
              variant="accent"
              disabled={isLoading || isListLoading}
            >
              Submit
            </Button>
            <Button
              type="button"
              variant="light-grey"
              className="cancel-modal-apply"
              disabled={isLoading}
              onClick={() => {
                handleReset();
                onClose?.();
              }}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <form
          id={APPLY_LEAVE_FORM_ID}
          noValidate
          onSubmit={handleSubmit}
          className="position-relative"
        >
          {isListLoading && (
            <div className={styles.overlay}>
              <div className="d-flex align-items-center justify-content-center p-4">
                <TailSpin
                  height="100"
                  width="100"
                  radius="1"
                  color="#0675d6"
                  ariaLabel="tail-spin-loading"
                  visible
                />
              </div>
            </div>
          )}
          {isLoading && (
            <div className={styles.overlay}>
              <ThreeDots
                height="48"
                width="48"
                radius="9"
                color="#0675d6"
                ariaLabel="loading"
                visible
              />
            </div>
          )}
          <div className="modal-body p-0">
            <div className="row mt-3">
              <div className="col-md-2"></div>
              <div className="col-md-3">
                <label
                  htmlFor={`${APPLY_LEAVE_TYPE_ID}-trigger`}
                  className="has-asterisk"
                >
                  Type of leave
                </label>
              </div>
              <div className="col-md-5">
                <div className={styles.validityAnchor}>
                  <select
                    ref={leaveTypeValidityRef}
                    className={styles.nativeValidityProxyField}
                    tabIndex={-1}
                    aria-hidden
                    value={formData.selectedLeaveType}
                    onChange={() => { }}
                    required={!isListLoading}
                    disabled={isListLoading}
                  >
                    <option value="" disabled hidden>
                      {isListLoading ? "Loading leave types…" : "Select Leave Type"}
                    </option>
                    {leaveTypeOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <Select
                    id={APPLY_LEAVE_TYPE_ID}
                    disabled={isListLoading}
                    placeholder={
                      isListLoading ? "Loading leave types…" : "Select Leave Type"
                    }
                    value={formData.selectedLeaveType}
                    onChange={(value) =>
                      handleInputChange({
                        target: { name: "selectedLeaveType", value, type: "text" },
                      })
                    }
                    options={leaveTypeOptions}
                  />
                </div>
                {shouldShowLeaveBalanceBadge(formData.selectedLeaveType) && (
                  <div className={`mt-2 ${styles.balanceBadgeRow}`}>
                    {getLeaveBalance(formData.selectedLeaveType)?.map((item) => (
                      <Badge key={item.key} variant="warning">
                        {formatLeaveTypeLabel(formData.selectedLeaveType)} :{" "}
                        {item.balance}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-md-2"></div>
            </div>

            <div className="row mt-3">
              <div className="col-md-2"></div>
              <div className="col-md-3">
                <label
                  htmlFor={APPLY_LEAVE_DATE_RANGE_ID}
                  className="has-asterisk"
                >
                  Date Range
                </label>
              </div>
              <div className="col-md-5">
                <div className={styles.validityAnchor}>
                  <input
                    ref={dateRangeValidityRef}
                    type="text"
                    className={styles.nativeValidityProxyField}
                    tabIndex={-1}
                    aria-hidden
                    autoComplete="off"
                    inputMode="none"
                    value={dateRangeComplete ? "ok" : ""}
                    onChange={() => { }}
                    onKeyDown={(ev) => ev.preventDefault()}
                    onPaste={(ev) => ev.preventDefault()}
                    onDrop={(ev) => ev.preventDefault()}
                    required={!isListLoading}
                    disabled={isListLoading}
                  />
                  <DateRange
                    id={APPLY_LEAVE_DATE_RANGE_ID}
                    startDate={
                      formData.startDate
                        ? formData.startDate instanceof Date
                          ? formData.startDate
                          : new Date(formData.startDate)
                        : null
                    }
                    endDate={
                      formData.endDate
                        ? formData.endDate instanceof Date
                          ? formData.endDate
                          : new Date(formData.endDate)
                        : null
                    }
                    onChange={onChange}
                    minDate={minDate}
                    maxDate={maxDate}
                    filterDate={filterLeaveDate}
                    placeholderText="mm/dd/yyyy - mm/dd/yyyy"
                    formatDisplayDate={formatDateMMDDYYYY}
                    pickerDateFormat="MM/dd/yyyy"
                    disabled={isListLoading}
                  />
                </div>
                {errors.startDate && (
                  <small className="text-danger d-block">{errors.startDate}</small>
                )}
                {errors.endDate && (
                  <small className="text-danger d-block">{errors.endDate}</small>
                )}
                <small className={styles.applyLeaveNote}>
                  Note: The start and end dates are included in the leave.
                </small>
              </div>
              <div className="col-md-2"></div>
            </div>

            <div className="row mt-3">
              <div className="col-md-2"></div>
              <div className="col-md-3">
                <label htmlFor="reasonOfLeave" className="has-asterisk">
                  Reason of Leave
                </label>
              </div>
              <div className="col-md-5">
                <TextArea
                  id="reasonOfLeave"
                  name="reasonOfLeave"
                  value={formData.reasonOfLeave}
                  onChange={handleInputChange}
                  rows={2}
                  required
                  error={errors.reasonOfLeave}
                />
              </div>
              <div className="col-md-2"></div>
            </div>
          </div>
        </form>
      </Modal>

      <Modal
        open={isConfirmModalOpen}
        onClose={handleConfirmNo}
        title="Confirm"
        size="md"
        closeOnBackdropClick={false}
        footer={
          formData.selectedLeaveType === "HalfYearly" ? (
            <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap w-100">
              <Button type="button" variant="accent" onClick={handleConfirmNo}>
                OK
              </Button>
            </div>
          ) : (
            <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap w-100">
              <Button type="button" variant="accent" onClick={handleConfirmYes}>
                Yes
              </Button>
              <Button
                type="button"
                variant="light-grey"
                onClick={handleConfirmNo}
              >
                No
              </Button>
            </div>
          )
        }
      >
        <p className="body-sm mb-0 text-center">
          {formData.selectedLeaveType === "HalfYearly" &&
            "You have an insufficient leave balance. Please get in touch with HR."}
          {formData.selectedLeaveType === "HalfDayLeave" &&
            "Are you sure you want to continue, as your Privilege Leave balance is zero? If you apply for Half Day Leave, the deduction will be made from your salary."}
          {(isPrivilegeLeaveType(formData.selectedLeaveType) ||
            isWorkFromHomeLeaveType(formData.selectedLeaveType) ||
            iscasualLeaveType(formData.selectedLeaveType) ||
            isQuarterlyLeaveType(formData.selectedLeaveType) ||
            isShortLeaveType(formData.selectedLeaveType)) &&
            "Are you sure you want to continue with the selected leave type since your leave balance is zero?"}
        </p>
      </Modal>
    </>
  );
};

export default ApplyLeave;

const ApplyLeaveSchema = Yup.object().shape({
  selectedLeaveType: Yup.string()
    .trim()
    .required("Type of leave is required."),
  startDate: Yup.date().nullable().required("Start Date is required"),
  endDate: Yup.date()
    .nullable()
    .required("End Date is required")
    .when("startDate", (startDate, schema) => {
      return schema.test({
        test: (endDate) => {
          return !startDate || new Date(endDate) >= new Date(startDate);
        },
        message: "'End Date' must be later than or equal to 'Start Date'",
      });
    }),
  reasonOfLeave: Yup.string()
    .trim()
    .min(5, "Reason must be at least 5 characters long.")
    .required("Reason is required."),
});
